/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';
import React from 'react';
import { toast } from 'react-toastify';

import {
  Button,
  DateInput,
  Dimmer,
  Form,
  Icon,
  Loader,
  Modal,
} from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { SectionHeader } from '@/components/Common';
import { getInseeParametrage } from '@/routes';
import type { ParametrageInsee } from '@/types';

const PARAMETRAGE_VIDE: ParametrageInsee = {
  transfertDematerialise: false,
  sdfiActif: false,
  envoiTest: false,
  proxyActif: false,
};

/** Port du proxy par défaut (_PROXY_PORT du legacy) */
const PORT_PROXY_DEFAUT = 80;

/** Champs texte envoyés tels quels (vide → null) */
const CHAMPS_TEXTE = [
  'conventionNumero',
  'conventionDate',
  'origineIdentifiant',
  'origineNom',
  'origineContact',
  'origineTelephone',
  'origineMail',
  'sdfiLogin',
  'motDePasse',
  'sdfiUrl',
  'proxyLogin',
  'proxyMotDePasse',
  'proxyAdresse',
] as const;

/** Obligatoires pour transmettre à l'INSEE, contrôlés quand la liaison est active */
const CHAMPS_OBLIGATOIRES = [
  'conventionNumero',
  'conventionDate',
  'origineIdentifiant',
  'origineNom',
  'origineContact',
  'origineTelephone',
  'origineMail',
  'sdfiLogin',
  'motDePasse',
  'sdfiUrl',
] as const;

const nettoyer = (valeur: unknown) =>
  _.isString(valeur) ? _.trim(valeur) || null : (valeur ?? null);

interface ModalParametresInseeProps {
  onClose: () => void;
}

/**
 * Paramétrage du transfert INSEE de l'entité : convention, informations de la
 * mairie (émetteur), connexion au SDFI v2 et proxy. Le récepteur (INSEE) et
 * les autres réglages WinDev restent en base, sans être modifiés ici.
 */
const ModalParametresInsee = ({ onClose }: ModalParametresInseeProps) => {
  const [parametrage, setParametrage] =
    React.useState<ParametrageInsee>(PARAMETRAGE_VIDE);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [erreurs, setErreurs] = React.useState<Record<string, string>>({});
  const [motsDePasseVisibles, setMotsDePasseVisibles] = React.useState<
    Record<string, boolean>
  >({});

  React.useEffect(() => {
    request
      .get(getInseeParametrage())
      .then((response: any) => {
        setParametrage({ ...PARAMETRAGE_VIDE, ...response });
      })
      .catch(() => {
        toast.error('Erreur lors de la récupération du paramétrage INSEE');
      })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = React.useCallback(
    (_e: React.SyntheticEvent, { name, value }: any) => {
      setParametrage((prev) => ({ ...prev, [name]: value }));
      setErreurs((prev) => ({ ...prev, [name]: '' }));
    },
    [],
  );

  // Liaison active : transfert dématérialisé par le module D2R2 (SDFI v2),
  // seul mode de transfert proposé par 360.
  const liaisonActive =
    !!parametrage.transfertDematerialise && !!parametrage.sdfiActif;

  const handleLiaison = React.useCallback(
    (_e: React.SyntheticEvent, { checked }: any) => {
      setParametrage((prev) => ({
        ...prev,
        transfertDematerialise: !!checked,
        sdfiActif: !!checked,
      }));
      if (!checked) setErreurs({});
    },
    [],
  );

  const handleChangeDate = React.useCallback(
    (date: string | Date | undefined) => {
      setParametrage((prev) => ({
        ...prev,
        conventionDate: date ? dayjs(date).format('YYYY-MM-DD') : null,
      }));
      setErreurs((prev) => ({ ...prev, conventionDate: '' }));
    },
    [],
  );

  const handleSave = React.useCallback(() => {
    if (liaisonActive) {
      const manquants = _.filter(
        CHAMPS_OBLIGATOIRES,
        (cle) => !nettoyer(parametrage[cle]),
      );
      if (!_.isEmpty(manquants)) {
        setErreurs(
          _.fromPairs(_.map(manquants, (cle) => [cle, 'Champ obligatoire'])),
        );
        toast.error('Renseignez les champs obligatoires');
        return;
      }
    }

    setSaving(true);
    setErreurs({});

    // Proxy utilisé dès qu'une adresse est renseignée
    const proxyActif = !!nettoyer(parametrage.proxyAdresse);
    const port = parametrage.proxyPort
      ? Number(parametrage.proxyPort)
      : proxyActif
        ? PORT_PROXY_DEFAUT
        : null;

    request
      .put(getInseeParametrage(), {
        transfertDematerialise: liaisonActive,
        sdfiActif: liaisonActive,
        proxyActif,
        proxyPort: port,
        ..._.fromPairs(
          CHAMPS_TEXTE.map((cle) => [cle, nettoyer(parametrage[cle])]),
        ),
      })
      .then(() => {
        toast.success('Paramétrage INSEE enregistré');
        onClose();
      })
      .catch((error: any) => {
        // Erreurs de validation de l'API (422) : affichées sous les champs
        const champs =
          error?.response?.data?.errors ?? error?.data?.errors ?? null;

        if (champs) {
          setErreurs(
            Object.fromEntries(
              Object.entries(champs).map(([champ, messages]) => [
                champ,
                String((messages as string[])[0] ?? ''),
              ]),
            ),
          );
        }

        toast.error("Erreur lors de l'enregistrement du paramétrage INSEE");
      })
      .finally(() => setSaving(false));
  }, [parametrage, liaisonActive, onClose]);

  const desactive = !liaisonActive || saving;

  const champ = (
    name: keyof ParametrageInsee,
    label: string,
    options: Record<string, unknown> = {},
  ) => (
    <Form.Input
      name={name}
      label={label}
      data-testid={`parametres-insee-${_.kebabCase(name)}`}
      value={(parametrage[name] as string | number | null) ?? ''}
      error={erreurs[name] || undefined}
      disabled={desactive}
      onChange={handleChange}
      {...options}
    />
  );

  // Pré-rempli avec le mot de passe enregistré (renvoyé déchiffré), masqué par
  // défaut ; vide quand il ne peut pas être relu (format WinDev non migré).
  const motDePasse = (
    name: 'motDePasse' | 'proxyMotDePasse',
    label: string,
    options: Record<string, unknown> = {},
  ) => {
    const visible = !!motsDePasseVisibles[name];

    return champ(name, label, {
      type: visible ? 'text' : 'password',
      autoComplete: 'new-password',
      placeholder: parametrage[`${name}Erreur`] ? 'À ressaisir' : '',
      icon: (
        <Icon
          link
          name={visible ? 'eye slash' : 'eye'}
          title={
            visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'
          }
          data-testid={`parametres-insee-${_.kebabCase(name)}-afficher`}
          onClick={() =>
            setMotsDePasseVisibles((prev) => ({ ...prev, [name]: !visible }))
          }
        />
      ),
      ...options,
    });
  };

  const formulaire = (
    <Form>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
        <SectionHeader title="Convention INSEE" style={{ margin: 0 }} />
        <Form.Checkbox
          label="Liaison active"
          data-testid="parametres-insee-liaison-active"
          checked={liaisonActive}
          disabled={saving}
          onChange={handleLiaison}
          style={{ margin: 0 }}
        />
      </div>
      <Form.Group style={{ marginTop: '1em' }}>
        {champ('conventionNumero', 'Convention numéro', {
          width: 3,
          required: true,
        })}
        <Form.Field
          width={2}
          required
          disabled={desactive}
          error={!!erreurs.conventionDate}
        >
          <label>Signée le</label>
          <DateInput
            data-testid="parametres-insee-convention-date"
            value={
              parametrage.conventionDate
                ? new Date(parametrage.conventionDate)
                : undefined
            }
            readOnly={desactive}
            error={!!erreurs.conventionDate}
            onChange={handleChangeDate}
          />
        </Form.Field>
      </Form.Group>

      <SectionHeader title="Informations de la Mairie" />
      <Form.Group>
        {champ('origineIdentifiant', 'Numéro SIRET', {
          width: 3,
          required: true,
        })}
        {champ('origineNom', 'Nom de la commune', { width: 4, required: true })}
        {champ('origineContact', 'Nom du contact', {
          width: 4,
          required: true,
        })}
        {champ('origineTelephone', 'Téléphone', { width: 2, required: true })}
        {champ('origineMail', 'Courriel', { width: 3, required: true })}
      </Form.Group>

      <SectionHeader title="Connexion SDFI V2" />
      <Form.Group>
        {champ('sdfiLogin', 'Identifiant de connexion', {
          width: 3,
          required: true,
        })}
        {motDePasse('motDePasse', 'Mot de passe', { width: 4, required: true })}
        {champ('sdfiUrl', "Url d'accès", { width: 8, required: true })}
      </Form.Group>

      <SectionHeader title="Proxy" />
      <Form.Group>
        {champ('proxyLogin', 'Nom utilisateur', { width: 3 })}
        {motDePasse('proxyMotDePasse', 'Mot de passe', { width: 4 })}
        {champ('proxyAdresse', 'Adresse IP', { width: 3 })}
        {champ('proxyPort', 'Port', {
          width: 1,
          type: 'number',
          placeholder: String(PORT_PROXY_DEFAUT),
        })}
      </Form.Group>
    </Form>
  );

  return (
    <Modal
      open
      size="large"
      style={{ width: 1280, maxWidth: '95vw' }}
      data-testid="parametres-insee-modal"
    >
      <Modal.Header>Paramètres INSEE</Modal.Header>
      <Modal.Content style={{ position: 'relative', minHeight: '10em' }}>
        {loading ? (
          <Dimmer active inverted>
            <Loader inverted>Chargement des paramètres</Loader>
          </Dimmer>
        ) : (
          formulaire
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button
          basic
          size="tiny"
          icon="cancel"
          content="Annuler"
          data-testid="parametres-insee-annuler"
          disabled={saving}
          onClick={onClose}
        />
        <Button
          positive
          icon="check"
          content="Enregistrer"
          data-testid="parametres-insee-enregistrer"
          loading={saving}
          disabled={loading || saving}
          onClick={handleSave}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalParametresInsee;
